import classNames from "classnames";
import {
  JSXElementConstructor,
  LabelHTMLAttributes,
  memo,
  ReactNode,
} from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { DescriptionText, ErrorText, LabelText } from "../common/descriptors";
import * as redoTextInputCss from "./base-redo-input.module.css";
import {
  RedoInputSize,
  RedoInputState,
  sizeStyles,
  sizeToDescriptorTextProps,
  sizeToInputTextProps,
  sizeToPx,
} from "./base-redo-text-input";

export type RedoInputAdornment =
  | { type: "text"; value: string }
  | { Icon: JSXElementConstructor<any>; type: "icon" };

export interface BaseRedoInputContainerProps {
  size?: RedoInputSize;
  state?: RedoInputState;
  label?: string;
  description?: string;
  errorText?: string;
  className?: string;
  children: ReactNode | ReactNode[];
  labelProps?: LabelHTMLAttributes<HTMLLabelElement>;
  dangerousStyleThatShouldOnlyBeUsedForMerchantBranding?: React.CSSProperties;
  fullWidth?: boolean;
  dynamicHeight?: boolean;
  uiSelector?: string;

  leadingAdornment?: RedoInputAdornment;
  trailingAdornment?: RedoInputAdornment;
}

export const BaseRedoInputContainer = memo(function BaseRedoInputContainer({
  size = "sm",
  state = "default",
  label,
  description,
  errorText,
  className,
  labelProps,
  dangerousStyleThatShouldOnlyBeUsedForMerchantBranding,
  children,
  fullWidth,
  dynamicHeight,
  uiSelector,

  leadingAdornment,
  trailingAdornment,
}: BaseRedoInputContainerProps) {
  const descriptorFontSize = sizeToDescriptorTextProps[size];

  const error = state === "error";
  const disabled = state === "disabled";
  const readonly = state === "readonly";
  const leadingAdornmentContent = renderRedoInputAdornment(
    leadingAdornment,
    size,
  );
  const trailingAdornmentContent = renderRedoInputAdornment(
    trailingAdornment,
    size,
  );

  return (
    <Flex
      data-ui={uiSelector}
      dir="column"
      gap="sm"
      minW="none"
      w={fullWidth ? "full" : undefined}
    >
      <LabelText label={label} size={descriptorFontSize} />
      <Flex align="center" gap="none" p="none">
        {leadingAdornmentContent && (
          <Flex
            align="center"
            alignSelf="stretch"
            bgColor="disabled"
            className={classNames(redoTextInputCss.prefix, sizeStyles[size])}
            pl="md"
            pr="sm"
            py="xs"
            shrink={0}
            textColor="quaternary"
          >
            {leadingAdornmentContent}
          </Flex>
        )}
        <Flex
          align="center"
          as="label"
          bgColor="primary"
          className={classNames(
            disabled && redoTextInputCss.disabled,
            redoTextInputCss.inputWrapper,
            error && redoTextInputCss.error,
            !dynamicHeight && sizeStyles[size],
            readonly && redoTextInputCss.readonly,
            leadingAdornmentContent && redoTextInputCss.hasPrefix,
            trailingAdornmentContent && redoTextInputCss.hasSuffix,
            className,
          )}
          gap="none"
          grow={1}
          labelProps={labelProps}
          minW="none"
          px={readonly ? undefined : sizeToPx[size]}
          style={{
            backgroundColor: readonly ? "transparent" : undefined,
            ...dangerousStyleThatShouldOnlyBeUsedForMerchantBranding,
          }}
          textColor="primary"
        >
          {children}
        </Flex>
        {trailingAdornmentContent && (
          <Flex
            align="center"
            alignSelf="stretch"
            bgColor="disabled"
            className={classNames(redoTextInputCss.suffix, sizeStyles[size])}
            pl="sm"
            pr="md"
            py="xs"
            shrink={0}
            textColor="quaternary"
          >
            {trailingAdornmentContent}
          </Flex>
        )}
      </Flex>
      <DescriptionText
        description={description}
        error={error}
        size={descriptorFontSize}
      />
      <ErrorText size={descriptorFontSize} text={errorText} />
    </Flex>
  );
});

export function getTextAdornment(
  value: string | undefined,
): RedoInputAdornment | undefined {
  return value ? { type: "text", value } : undefined;
}

export function renderRedoInputAdornment(
  adornment: RedoInputAdornment | undefined,
  size: RedoInputSize,
) {
  if (!adornment) {
    return undefined;
  }

  if (adornment.type === "icon") {
    const Icon = adornment.Icon;
    return <Icon />;
  }

  return (
    <Text fontSize={sizeToInputTextProps[size].fontSize}>
      {adornment.value}
    </Text>
  );
}
